<template>
  <div
    class="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 px-4"
  >
    <div class="w-full max-w-md space-y-6">
      <!-- Encabezado -->
      <div class="text-center space-y-2">
        <div
          class="inline-flex items-center gap-2 rounded-2xl bg-white/5 border border-white/10 px-4 py-2 backdrop-blur-sm shadow-sm"
        >
          <span class="h-2 w-2 rounded-full bg-emerald-400 animate-pulse"></span>
          <span
            class="text-[11px] uppercase tracking-[0.25em] text-slate-300"
          >
            Almacén
          </span>
        </div>

        <div>
          <h1 class="mt-4 text-3xl font-semibold text-slate-50">
            Iniciar sesión
          </h1>
          <p class="mt-1 text-sm text-slate-400">
            Ingresa tu correo para continuar.
          </p>
        </div>
      </div>

      <!-- Tarjeta principal (glass) -->
      <div
        class="rounded-3xl bg-white/5 border border-white/10 shadow-[0_18px_45px_rgba(15,23,42,0.85)] backdrop-blur-2xl px-6 py-7 space-y-5"
      >
        <form @submit.prevent="handleFormSubmit" class="space-y-5">
        <!-- Paso 1: Email -->
        <transition name="fade" @after-leave="afterEmailFade">
          <div
            v-if="!emailConfirmed && !animating"
            class="space-y-3"
          >
            <label
              class="block text-xs font-medium uppercase tracking-[0.18em] text-slate-300"
            >
              Correo electrónico
            </label>

            <div class="space-y-2">
              <input
                v-model.trim="email"
                type="email"
                autocomplete="email"
                placeholder="nombre@empresa.com"
                class="ios-input"
                @input="resetAnimation"

              />

              <!-- 🔄 AHORA ESTE BOTÓN ES SUBMIT -->
              <button
                type="submit"
                class="ios-primary-button w-full flex items-center justify-center gap-2"
                :disabled="alreadyLoggingIn"
              >
                <span v-if="!alreadyLoggingIn">Continuar</span>
                <span v-else class="loader-dot"></span>
              </button>
            </div>

            <p class="text-xs text-slate-400">
              Usaremos este correo para identificar tu cuenta.
            </p>
          </div>
        </transition>

          <!-- Chip que muestra el correo confirmado -->
          <transition name="move-up" @after-enter="afterEmailMoveUp">
            <button
              v-if="emailConfirmed"
              type="button"
              class="flex items-center justify-between w-full rounded-2xl bg-white/8 border border-white/15 px-4 py-3 text-left text-slate-100 hover:bg-white/[0.12] transition"
              @click="editEmail"
            >
              <div class="flex flex-col">
                <span
                  class="text-[11px] uppercase tracking-[0.16em] text-slate-400"
                >
                  Correo
                </span>
                <span class="text-sm font-medium truncate">
                  {{ email }}
                </span>
              </div>
              <span class="text-[11px] text-slate-400">
                Cambiar
              </span>
            </button>
          </transition>

          <!-- Paso 2: Password -->
          <transition name="slide-fade">
            <div
              v-if="showPasswordInput"
              :class="{
                'opacity-100 translate-y-0': passActive,
                'opacity-0 translate-y-2': !passActive
              }"
              class="space-y-3 transform transition duration-300"
            >
              <label
                class="block text-xs font-medium uppercase tracking-[0.18em] text-slate-300"
              >
                Contraseña
              </label>

              <div class="space-y-2">
                <input
                  v-model="password"
                  type="password"
                  autocomplete="current-password"
                  class="ios-input"
                  placeholder="••••••••"

                />

                <!-- 🔄 ESTE TAMBIÉN ES SUBMIT Y SIN @click -->
                <button
                  type="submit"
                  class="ios-primary-button w-full flex items-center justify-center gap-2"
                  :disabled="alreadyLoggingIn"
                >
                  <span v-if="!alreadyLoggingIn">Iniciar sesión</span>
                  <span v-else class="loader-dot"></span>
                </button>
              </div>

              <p
                v-if="loginError"
                class="text-xs text-rose-400"
              >
                {{ loginError }}
              </p>
            </div>
          </transition>

        </form>

        <!-- Olvidé contraseña -->
        <div class="pt-1 text-center">
          <button
            type="button"
            class="text-xs font-medium text-slate-300 hover:text-slate-100 transition"
            @click="navegarARestablecerAcceso"
          >
            ¿Olvidaste la contraseña?
          </button>
        </div>
      </div>

      <!-- Tarjeta crear cuenta -->
      <div
        class="rounded-3xl border border-white/10 bg-black/20 backdrop-blur-xl px-6 py-5 text-center shadow-[0_18px_45px_rgba(15,23,42,0.85)]"
      >
        <p class="text-sm text-slate-200 mb-3">
          ¿No tienes una cuenta?
        </p>
        <button
          type="button"
          class="ios-secondary-button w-full"
          @click="goToRegister"
        >
          Crear una cuenta
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { loginRequest } from './service/login.service';
import { useToast } from 'vue-toastification';
import { usePermissions } from '@/plugins/permissions'; 

export default {
  name: 'UserLogin',

  // Usamos Composition API solo para obtener el toast y exponerlo al this.*
  setup() {
    const toast = useToast();
     const { reloadRole } = usePermissions(); 
    return { toast, reloadRole };
  },

  data() {
    return {
      email: '',
      password: '',
      emailConfirmed: false,
      showPasswordInput: false,
      animating: false,
      passActive: false,
      alreadyLoggingIn: false,
      loginError: null,
    };
  },

  mounted() {
    // Para comprobar que ahora sí tenemos instancia de toast
    console.log('Toast en UserLogin (useToast):', this.toast);
  },

  methods: {
    ...mapActions(['login']),

    handleFormSubmit() {
      // si ya estamos logueando, no hagas nada
      if (this.alreadyLoggingIn) return;

      this.loginError = null;

      if (!this.emailConfirmed) {
        // Paso 1: solo animación del email
        this.startEmailAnimation();
      } else {
        // Paso 2: ahora sí login real
        this.handleLogin();
      }
    },

    startEmailAnimation() {
      this.loginError = null;

      if (this.validateEmail(this.email)) {
        this.animating = true;
      } else {
        this.showError('Por favor, introduce un correo electrónico válido.');
      }
    },

    validateEmail(email) {
      const re =
        /^([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return re.test(String(email).toLowerCase());
    },

    afterEmailFade() {
      this.emailConfirmed = true;
      this.showPasswordInput = true;

      setTimeout(() => {
        this.passActive = true;
      }, 80);
    },

    afterEmailMoveUp() {
      this.animating = false;
    },

    editEmail() {
      this.password = '';
      this.loginError = null;
      this.emailConfirmed = false;
      this.showPasswordInput = false;
      this.passActive = false;
    },

    resetAnimation() {
      if (this.emailConfirmed || this.showPasswordInput) {
        this.emailConfirmed = false;
        this.showPasswordInput = false;
        this.passActive = false;
      }
      this.loginError = null;
    },

    async handleLogin() {
      if (this.alreadyLoggingIn) return;

      if (!this.password) {
        this.showError('Ingresa tu contraseña.');
        return;
      }

      this.alreadyLoggingIn = true;
      this.loginError = null;

      try {
        const data = await loginRequest({
          email: this.email,
          password: this.password,
        });

        if (data && data.success) {
              // 1) Guardar usuario (esto pone user y roleToken en el store)
          await this.login(data.user);

          // 2) Verificar rol INMEDIATAMENTE usando el plugin
          if (typeof this.reloadRole === 'function') {
            // force = true para que sí llame a /api/verify-role aunque haya caché previa
            await this.reloadRole(this.$store, true);
          }
          this.showSuccess('Sesión iniciada correctamente.');
          this.$router.push('/dashboard');
        } else {
          this.showError(data?.error || 'Correo o contraseña incorrectos.');
        }
      } catch (error) {
        console.error('Error de inicio de sesión:', error);
        this.showError('Error en el servidor, intenta de nuevo más tarde.');
      } finally {
        this.alreadyLoggingIn = false;
      }
    },

    goToRegister() {
      this.$router.push('/registrarse');
    },

    navegarARestablecerAcceso() {
      this.$router.push('/restablecer-acceso');
    },

    // Helpers para toast y mensaje bajo el input
    showError(message) {
      this.loginError = message;

      if (this.toast) {
        this.toast.error(message, {
          timeout: 4000,
        });
      }
    },

    showSuccess(message) {
      if (this.toast) {
        this.toast.success(message, {
          timeout: 2500,
        });
      }
    },
  },
};
</script>



<style scoped>
/* Inputs estilo iOS / glass */
.ios-input {
  width: 100%;
  border-radius: 1rem;
  background: rgba(15, 23, 42, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.18);
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  color: #e5e7eb;
  outline: none;
  transition:
    border-color 0.18s ease,
    box-shadow 0.18s ease,
    background-color 0.18s ease,
    transform 0.12s ease;
}

.ios-input::placeholder {
  color: rgba(148, 163, 184, 0.9);
}

.ios-input:focus {
  border-color: rgba(52, 211, 153, 0.95);
  box-shadow:
    0 0 0 1px rgba(52, 211, 153, 0.7),
    0 18px 45px rgba(15, 23, 42, 0.85);
  background: rgba(15, 23, 42, 0.98);
  transform: translateY(-1px);
}

/* Botones principales */
.ios-primary-button {
  border-radius: 999px;
  padding: 0.7rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  background: linear-gradient(
    135deg,
    #f9fafb,
    #e5e7eb
  );
  color: #020617;
  border: none;
  box-shadow:
    0 12px 30px rgba(15, 23, 42, 0.7),
    0 0 0 1px rgba(148, 163, 184, 0.3);
  cursor: pointer;
  transition:
    transform 0.12s ease,
    box-shadow 0.18s ease,
    background 0.18s ease,
    opacity 0.18s ease;
}

.ios-primary-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow:
    0 16px 36px rgba(15, 23, 42, 0.85),
    0 0 0 1px rgba(148, 163, 184, 0.45);
}

.ios-primary-button:active:not(:disabled) {
  transform: translateY(0);
  box-shadow:
    0 10px 25px rgba(15, 23, 42, 0.9),
    0 0 0 1px rgba(148, 163, 184, 0.35);
}

.ios-primary-button:disabled {
  opacity: 0.7;
  cursor: default;
}

/* Botón secundario (crear cuenta) */
.ios-secondary-button {
  border-radius: 999px;
  padding: 0.7rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  background: rgba(15, 23, 42, 0.8);
  color: #e5e7eb;
  border: 1px solid rgba(148, 163, 184, 0.6);
  cursor: pointer;
  transition:
    transform 0.12s ease,
    box-shadow 0.18s ease,
    background-color 0.18s ease,
    border-color 0.18s ease;
}

.ios-secondary-button:hover {
  background: rgba(15, 23, 42, 0.95);
  border-color: rgba(148, 163, 184, 0.9);
  box-shadow: 0 14px 32px rgba(15, 23, 42, 0.85);
}

.ios-secondary-button:active {
  transform: translateY(0);
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.9);
}

/* Loader pequeño dentro del botón */
.loader-dot {
  width: 0.9rem;
  height: 0.9rem;
  border-radius: 999px;
  border: 2px solid rgba(15, 23, 42, 0.85);
  border-top-color: rgba(15, 23, 42, 0.1);
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Transiciones de Vue (email → password) */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.move-up-enter-active {
  transition: all 0.25s ease;
}

.move-up-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>
